import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRandomContent, recordProgress, awardBonus, Content } from '../services/api';
import { speak } from '../services/audio';
import './Games.css';

const FillGapGame: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lessonId = searchParams.get('lessonId') || undefined;
  const customSetId = searchParams.get('customSetId') || undefined;

  const [items, setItems] = useState<Content[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [gapWord, setGapWord] = useState('');
  const [sentenceWithGap, setSentenceWithGap] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  const createGapSentence = useCallback((sentence: string, allItems: Content[]) => {
    const words = sentence.split(' ');
    if (words.length < 3) return null;
    
    // Pick a random word to remove (not first or last, prefer content words)
    const validIndices = words
      .map((w, i) => ({ word: w, index: i }))
      .filter(({ word, index }) => {
        const clean = word.replace(/[.,!?]/g, '').toLowerCase();
        // Skip articles, pronouns, short words
        const skipWords = ['a', 'an', 'the', 'i', 'is', 'am', 'are', 'to', 'in', 'on', 'at'];
        return clean.length >= 3 && !skipWords.includes(clean) && index > 0 && index < words.length - 1;
      });
    
    if (validIndices.length === 0) {
      // Fallback: pick any word except first
      const idx = Math.floor(Math.random() * (words.length - 1)) + 1;
      validIndices.push({ word: words[idx], index: idx });
    }
    
    const chosen = validIndices[Math.floor(Math.random() * validIndices.length)];
    const gapWord = chosen.word.replace(/[.,!?]/g, '');
    
    // Create sentence with gap
    const gapSentence = words.map((w, i) => i === chosen.index ? '_____' : w).join(' ');
    
    // Generate wrong options from other items
    const wrongOptions = allItems
      .filter(item => item.english !== sentence)
      .map(item => {
        const itemWords = item.english.split(' ');
        return itemWords[Math.floor(Math.random() * itemWords.length)].replace(/[.,!?]/g, '');
      })
      .filter(w => w.length >= 2 && w.toLowerCase() !== gapWord.toLowerCase())
      .slice(0, 3);
    
    // Ensure we have 3 wrong options
    while (wrongOptions.length < 3) {
      wrongOptions.push(['cat', 'dog', 'big', 'run', 'see', 'go'][wrongOptions.length]);
    }
    
    return { gapWord, gapSentence, wrongOptions: wrongOptions.slice(0, 3) };
  }, []);

  useEffect(() => {
    const loadItems = async () => {
      try {
        const data = await getRandomContent(50, lessonId, customSetId);
        // Filter for sentences (3+ words)
        const sentences = data.filter(item => item.english.split(' ').length >= 3);
        setItems(sentences.slice(0, 10));
      } catch (err) {
        console.error('Failed to load content:', err);
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, [lessonId, customSetId]);

  useEffect(() => {
    if (items.length > 0 && currentIndex < items.length) {
      const result = createGapSentence(items[currentIndex].english, items);
      if (result) {
        setGapWord(result.gapWord);
        setSentenceWithGap(result.gapSentence);
        const allOptions = [result.gapWord, ...result.wrongOptions];
        setOptions(allOptions.sort(() => Math.random() - 0.5));
      }
      setSelectedAnswer(null);
    }
  }, [items, currentIndex, createGapSentence]);

  const handleOptionClick = async (option: string) => {
    if (feedback) return;
    
    setSelectedAnswer(option);
    const isCorrect = option.toLowerCase() === gapWord.toLowerCase();
    
    setFeedback(isCorrect ? 'correct' : 'wrong');
    await recordProgress(items[currentIndex].id, isCorrect);
    
    speak(items[currentIndex].english);

    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setFeedback(null);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (score.correct + (feedback === 'correct' ? 1 : 0) === items.length) {
      await awardBonus(30, 'perfect_fillgap');
    }
    setCompleted(true);
  };

  if (loading) return <div className="loading">Ładowanie...</div>;
  if (items.length < 4) {
    return (
      <div className="game-error">
        <h2>Za mało zdań!</h2>
        <p>Wybierz lekcję ze zdaniami.</p>
        <button className="btn btn-primary" onClick={() => navigate('/games')}>Wróć</button>
      </div>
    );
  }

  const currentItem = items[currentIndex];
  if (!currentItem) return <div className="loading">Ładowanie...</div>;

  if (completed) {
    const isPerfect = score.correct === items.length;
    return (
      <div className="game-complete fade-in">
        <div className="complete-icon">{isPerfect ? '🏆' : '🎉'}</div>
        <h1>{isPerfect ? 'Perfekcyjnie!' : 'Świetnie!'}</h1>
        <p>Wynik: {score.correct} / {score.total}</p>
        {isPerfect && <div className="bonus-badge bounce">+30 bonus!</div>}
        <button className="btn btn-primary btn-large" onClick={() => window.location.reload()}>
          Zagraj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className="game-container fade-in">
      <div className="game-header">
        <h2>🧩 Uzupełnij luki</h2>
        <div className="game-progress">{currentIndex + 1} / {items.length}</div>
      </div>

      <div className="game-score">✅ {score.correct} / {score.total}</div>

      <div className="fillgap-card">
        <div className="polish-word">{currentItem.polish}</div>
        <p className="quiz-instruction">Wybierz brakujące słowo:</p>

        <div className={`gap-sentence ${feedback || ''}`}>
          {sentenceWithGap}
        </div>

        <div className="fillgap-options">
          {options.map((option, index) => (
            <button
              key={index}
              className={`option-btn ${selectedAnswer === option ? (feedback || '') : ''}`}
              onClick={() => handleOptionClick(option)}
              disabled={!!feedback}
            >
              {option}
            </button>
          ))}
        </div>

        {feedback === 'wrong' && (
          <div className="correct-answer">
            Poprawna odpowiedź: <strong>{gapWord}</strong>
            <button className="speak-btn-inline" onClick={() => speak(currentItem.english)}>🔊</button>
          </div>
        )}

        {feedback && (
          <button className="btn btn-primary btn-next" onClick={handleNext}>
            Dalej ➜
          </button>
        )}
      </div>
    </div>
  );
};

export default FillGapGame;

